import { supabase } from '../lib/supabase';
import { companyService } from './companyService';
import { stripeService } from './stripeService';
import { DEMO_ERROR_MESSAGE, isDemoUser } from '../utils/demoMode';

export const siteService = {
  async createSite(siteData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Demo mode guard — demo users cannot persist new sites; surface an
      // explicit error instead of fake success.
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }

      // Check if creating an active site
      if (siteData?.status === 'active') {
        const demoAdminEmail = import.meta.env?.VITE_DEMO_ADMIN_EMAIL;
        const isDemoAdmin = demoAdminEmail && user?.email === demoAdminEmail;

        if (!isDemoAdmin) {
          const { data: canAdd, error: limitError } = await companyService?.canAddActiveSite(siteData?.companyId);
          if (limitError) throw limitError;
          if (!canAdd) throw new Error('SITE_LIMIT_REACHED');
        }
      }

      // Trim string identifiers at the write boundary. site_name in particular
      // is later compared to CSV cells (which are already trimmed), so a stored
      // value with stray whitespace silently breaks CSV imports for tasks,
      // documents, and snags (DNllfNne).
      const { data, error } = await supabase?.from('sites')?.insert({
          company_id: siteData?.companyId,
          site_name: siteData?.siteName?.trim(),
          site_reference: siteData?.siteReference?.trim(),
          address_line1: siteData?.addressLine1?.trim(),
          address_line2: siteData?.addressLine2?.trim() || null,
          city: siteData?.city?.trim(),
          county: siteData?.county?.trim() || null,
          postcode: siteData?.postcode?.trim(),
          client_name: siteData?.clientName?.trim(),
          client_contact_person: siteData?.clientContactPerson?.trim(),
          client_email: siteData?.clientEmail?.trim(),
          client_phone: siteData?.clientPhone?.trim(),
          status: siteData?.status || 'planning',
          start_date: siteData?.startDate,
          end_date: siteData?.endDate,
          created_by: user?.id
        })?.select()?.single();

      if (error) throw error;


      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Default-filters archived sites out — every role view should treat
  // archived as invisible (NSherjFh cascade visibility rule). The one
  // caller that needs archived rows is the sites-list management page,
  // which passes `{ includeArchived: true }` to drive its archive /
  // unarchive toggle.
  async getAllSites(options = {}) {
    const { includeArchived = false } = options;
    try {
      let query = supabase
        ?.from('sites')
        ?.select('*')
        ?.order('created_at', { ascending: false });
      if (!includeArchived) query = query?.neq('status', 'archived');
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  // Selectable sites — for pickers / dropdowns where archived sites must
  // disappear but planning / on_hold / completed remain valid choices. Use
  // this when the user is choosing a site to write against (snag, invite,
  // worker assignment, task plan). Renamed from `getActiveSites` so the
  // name matches the broader semantics: only `archived` is invisible
  // (NSherjFh).
  async getSelectableSites() {
    try {
      const { data, error } = await supabase
        ?.from('sites')
        ?.select('*')
        ?.neq('status', 'archived')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  // Update the site_manager_id for a site — admin only
  // Also manages worker_site_assignments so site managers appear
  // in all site-scoped queries (schedule, tasks, etc.)
  async updateSiteManager(siteId, siteManagerId) {
    try {
      // Get the site's company_id for the assignment record
      const { data: site, error: siteError } = await supabase
        ?.from('sites')
        ?.select('company_id, site_manager_id')
        ?.eq('id', siteId)
        ?.single();
      if (siteError) throw siteError;

      const previousManagerId = site?.site_manager_id;
      const companyId = site?.company_id;

      // Update the site_manager_id column
      const { data, error } = await supabase
        ?.from('sites')
        ?.update({ site_manager_id: siteManagerId || null })
        ?.eq('id', siteId)
        ?.select()
        ?.single();
      if (error) throw error;

      // Remove previous manager's site assignment (if changing manager)
      if (previousManagerId && previousManagerId !== siteManagerId) {
        await supabase
          ?.from('worker_site_assignments')
          ?.delete()
          ?.eq('user_id', previousManagerId)
          ?.eq('site_id', siteId);
      }

      // Add new manager's site assignment
      if (siteManagerId && companyId) {
        await supabase
          ?.from('worker_site_assignments')
          ?.upsert(
            { user_id: siteManagerId, site_id: siteId, company_id: companyId },
            { onConflict: 'user_id,site_id' }
          );
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Scope sites to a specific company. Default-filters archived sites
  // (NSherjFh cascade visibility); pass `{ includeArchived: true }` from
  // the sites-list management view if you need the archive toggle.
  async getSitesByCompany(companyId, options = {}) {
    const { includeArchived = false } = options;
    try {
      if (!companyId) return await siteService?.getAllSites({ includeArchived });
      let query = supabase
        ?.from('sites')
        ?.select('*')
        ?.eq('company_id', companyId)
        ?.order('created_at', { ascending: false });
      if (!includeArchived) query = query?.neq('status', 'archived');
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  // Get the sites the current user is scoped to.
  //   - admin:        whole company (BwT3Rzhb: "Admins should see the whole
  //                    company"). Also keeps the unconditional callers —
  //                    RAMS / inductions / toolbox / photo gallery — working
  //                    for admins, which depend on this method.
  //   - site_manager: ONLY assigned sites (worker_site_assignments ∪
  //                    sites.site_manager_id). No company-wide fallback
  //                    (BwT3Rzhb) — zero assignments means zero sites.
  //   - worker:        same strict assignment scoping as site_manager.
  // Default-filters archived sites (NSherjFh cascade visibility); the
  // sites-list management view passes `{ includeArchived: true }`.
  async getSitesByUser(options = {}) {
    const { includeArchived = false } = options;
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Admins are company-wide by design — never assignment-scoped.
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('role')
        ?.eq('id', user?.id)
        ?.maybeSingle();

      if (profile?.role === 'admin') {
        const companyResult = await companyService?.getUserCompany();
        if (companyResult?.data?.id) {
          return await siteService?.getSitesByCompany(companyResult?.data?.id, { includeArchived });
        }
        return await siteService?.getAllSites({ includeArchived });
      }

      // site_manager / worker: strictly the sites they are assigned to.
      // 1. Site IDs from worker_site_assignments for this user
      const { data: assignments } = await supabase
        ?.from('worker_site_assignments')
        ?.select('site_id')
        ?.eq('user_id', user?.id);

      // 2. Also sites where the user is directly set as site_manager_id
      const { data: managedSites } = await supabase
        ?.from('sites')
        ?.select('id')
        ?.eq('site_manager_id', user?.id);

      const assignedSiteIds = (assignments || [])?.map(a => a?.site_id);
      const managedSiteIds = (managedSites || [])?.map(s => s?.id);
      const allSiteIds = [...new Set([...assignedSiteIds, ...managedSiteIds])];

      // No company-wide fallback (BwT3Rzhb). Zero assignments = zero sites;
      // the UI shows an explanatory empty state.
      if (allSiteIds?.length === 0) {
        return { data: [], error: null };
      }

      let query = supabase
        ?.from('sites')
        ?.select('*')
        ?.in('id', allSiteIds)
        ?.order('created_at', { ascending: false });
      if (!includeArchived) query = query?.neq('status', 'archived');
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  // Get workers assigned to a specific site via site_workers junction table
  async getWorkersBySite(siteId) {
    try {
      if (!siteId) return { data: [], error: null };

      // Get workers via site_workers → workers → user_profiles
      const { data: siteWorkers, error: swError } = await supabase
        ?.from('site_workers')
        ?.select(`
          worker_id,
          is_active,
          workers (
            id,
            user_profile_id,
            full_name,
            email,
            trade,
            is_active
          )
        `)
        ?.eq('site_id', siteId)
        ?.eq('is_active', true);

      // Also get users directly assigned via worker_site_assignments
      const { data: directAssignments, error: daError } = await supabase
        ?.from('worker_site_assignments')
        ?.select(`
          user_id,
          user_profiles:user_id (
            id,
            full_name,
            email,
            role,
            is_active,
            company_id
          )
        `)
        ?.eq('site_id', siteId);

      const workerProfiles = [];
      const seenIds = new Set();

      // Add workers from site_workers (via workers table)
      (siteWorkers || [])?.forEach(sw => {
        const w = sw?.workers;
        if (w?.user_profile_id && !seenIds?.has(w?.user_profile_id)) {
          seenIds?.add(w?.user_profile_id);
          workerProfiles?.push({
            id: w?.user_profile_id,
            full_name: w?.full_name,
            email: w?.email,
            role: 'worker',
            is_active: w?.is_active
          });
        }
      });

      // Add users from worker_site_assignments
      (directAssignments || [])?.forEach(da => {
        const up = da?.user_profiles;
        if (up?.id && !seenIds?.has(up?.id)) {
          seenIds?.add(up?.id);
          workerProfiles?.push(up);
        }
      });

      return { data: workerProfiles, error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  // Get workers for multiple sites (used by site_manager for assignment dropdowns)
  async getWorkersBySites(siteIds) {
    try {
      if (!siteIds?.length) return { data: [], error: null };

      const { data: directAssignments, error: daError } = await supabase
        ?.from('worker_site_assignments')
        ?.select(`
          user_id,
          site_id,
          user_profiles:user_id (
            id,
            full_name,
            email,
            role,
            is_active,
            company_id
          )
        `)
        ?.in('site_id', siteIds);

      const { data: siteWorkers, error: swError } = await supabase
        ?.from('site_workers')
        ?.select(`
          site_id,
          workers (
            user_profile_id,
            full_name,
            email,
            is_active
          )
        `)
        ?.in('site_id', siteIds)
        ?.eq('is_active', true);

      const workerProfiles = [];
      const seenIds = new Set();

      (directAssignments || [])?.forEach(da => {
        const up = da?.user_profiles;
        if (up?.id && !seenIds?.has(up?.id) && up?.is_active) {
          seenIds?.add(up?.id);
          workerProfiles?.push(up);
        }
      });

      (siteWorkers || [])?.forEach(sw => {
        const w = sw?.workers;
        if (w?.user_profile_id && !seenIds?.has(w?.user_profile_id) && w?.is_active) {
          seenIds?.add(w?.user_profile_id);
          workerProfiles?.push({
            id: w?.user_profile_id,
            full_name: w?.full_name,
            email: w?.email,
            role: 'worker',
            is_active: w?.is_active
          });
        }
      });

      return { data: workerProfiles, error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getSiteById(siteId) {
    try {
      const { data, error } = await supabase?.from('sites')?.select('*')?.eq('id', siteId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async updateSite(siteId, updates) {
    try {
      // If changing status to active, check limits
      if (updates?.status === 'active') {
        // Get current site to check if status is changing
        const { data: currentSite } = await supabase?.from('sites')?.select('status, company_id')?.eq('id', siteId)?.single();
        
        // Only check limit if status is changing FROM non-active TO active
        if (currentSite?.status !== 'active') {
          const { data: canAdd, error: limitError } = await companyService?.canAddActiveSite(currentSite?.company_id);
          if (limitError) throw limitError;
          if (!canAdd) throw new Error('SITE_LIMIT_REACHED');
        }
      }

      // Capture company_id and old status before update for sync
      const { data: preSite } = await supabase?.from('sites')?.select('status, company_id')?.eq('id', siteId)?.single();

      // Trim string identifiers when present in the updates payload, mirroring
      // createSite. Keeps stored site_name (and friends) free of stray
      // whitespace so downstream lookups (CSV imports, name comparisons) stay
      // consistent (DNllfNne).
      const TRIMMABLE_FIELDS = [
        'site_name', 'site_reference', 'address_line1', 'address_line2',
        'city', 'county', 'postcode',
        'client_name', 'client_contact_person', 'client_email', 'client_phone',
      ];
      const normalisedUpdates = { ...updates };
      TRIMMABLE_FIELDS?.forEach((f) => {
        if (typeof normalisedUpdates?.[f] === 'string') {
          normalisedUpdates[f] = normalisedUpdates[f]?.trim();
        }
      });

      const { data, error } = await supabase?.from('sites')?.update(normalisedUpdates)?.eq('id', siteId)?.select()?.single();
      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async deleteSite(siteId) {
    try {
      const { error } = await supabase?.from('sites')?.delete()?.eq('id', siteId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: { message: error?.message } };
    }
  },

  async archiveSite(siteId) {
    try {
      // Capture company_id before archiving
      const { data: preSite } = await supabase?.from('sites')?.select('company_id')?.eq('id', siteId)?.single();

      const { data, error } = await supabase
        ?.from('sites')
        ?.update({ status: 'archived' })
        ?.eq('id', siteId)
        ?.select()
        ?.single();

      if (error) throw error;


      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message, code: error?.code } };
    }
  },

  async unarchiveSite(siteId) {
    try {
      // Check plan limit before unarchiving to active
      const { data: currentSite } = await supabase
        ?.from('sites')
        ?.select('company_id')
        ?.eq('id', siteId)
        ?.single();

      if (currentSite?.company_id) {
        const { data: canAdd, error: limitError } = await companyService?.canAddActiveSite(currentSite?.company_id);
        if (limitError) throw limitError;
        if (!canAdd) throw new Error('SITE_LIMIT_REACHED');
      }

      const { data, error } = await supabase
        ?.from('sites')
        ?.update({ status: 'active' })
        ?.eq('id', siteId)
        ?.select()
        ?.single();

      if (error) throw error;


      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message, code: error?.code } };
    }
  }
};
